import React from 'react';
import { isChunkLoadError } from '../utils/lazyWithRetry';

/**
 * The in-shell error boundary (founder 2026-08-06: "when system errors out
 * due to an unknown exception, the user should never see the actual error —
 * a branded page saying an industry standard message").
 *
 * Distinct from RouteErrorBoundary (the app-shell last resort OUTSIDE the
 * router, which cannot reset and replaces the whole shell): this one wraps
 * the routed page CONTENT, so the nav, sidebar, toasts and Astra survive a
 * page crash — and mounting it with key={location.pathname} makes it reset
 * on navigation, so one bad render on one page never bricks the session.
 * The error object is logged for us, never rendered for them.
 */
export default class AppErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error('[AppErrorBoundary]', error, info?.componentStack);
        // Same telemetry as the shell boundary — the crashes this hides
        // from students must not also be hidden from us (review blocker).
        try {
            import('../services/analytics').then(({ track, EVENTS }) => {
                track(EVENTS.APP_ERROR_BOUNDARY, {
                    stale_build: isChunkLoadError(error),
                    // No free text (audit D1): error messages embed URLs and
                    // ids — unbounded cardinality and a PII risk. The NAME is
                    // the useful enum; the full message stays in console/logs.
                    error_kind: String(error?.name || 'Error').slice(0, 40),
                    path: window.location?.pathname,
                    boundary: 'in-shell',
                });
            }).catch(() => {});
        } catch { /* telemetry must never re-throw */ }
    }

    render() {
        if (this.state.error && isChunkLoadError(this.state.error)) {
            // Stale-build chunk errors belong to the OUTER RouteErrorBoundary,
            // whose "Stratia just updated / Reload" path (with the retry
            // budget reset) is the only real fix — "Try again" here would
            // re-render the cached rejected lazy forever (review blocker).
            throw this.state.error;
        }
        if (!this.state.error) return this.props.children;
        return (
            <div className="flex min-h-[60vh] items-center justify-center px-4"
                 data-testid="app-error-boundary">
                <div className="w-full max-w-md rounded-2xl border border-[#E0DED8] bg-[#FDFCF7] p-8 text-center">
                    <h2 className="font-serif text-xl font-semibold text-[#2C2C2C]">
                        Something went wrong on our side
                    </h2>
                    <p className="mt-2 text-sm text-[#6B6B6B]">
                        It&rsquo;s not you — a hiccup on our end stopped this page.
                        Please try again, or come back in a few minutes.
                    </p>
                    <button
                        type="button"
                        onClick={() => this.setState({ error: null })}
                        className="mt-5 rounded-full bg-[#1A4D2E] px-5 py-2 text-sm font-semibold text-white hover:bg-[#2D6B45]"
                    >
                        Try again
                    </button>
                </div>
            </div>
        );
    }
}
